import { useContext } from "react";
import Select, { SingleValue, StylesConfig } from "react-select";
import { AppContext } from "../../../contexts/AppContext";
import { NationalityOptionModel } from "../../../apis/ApisPersonalDetails";
import { FilterOptionNumbered } from "../Shared/SharedTypes";
import { RequiredIndicator } from "../Shared/RequiredIndicator";

export type NationalitySelectOption = FilterOptionNumbered & {
    shortName: string,
    iso: string,
}

export type NationalityDropdownProps = {
    nationalities: NationalityOptionModel[],
    selectedId: number | null,
    nationalityText: string | null,
    required: boolean,
    submitting: boolean,
    loading: boolean,
    onChange: (id: number | null, shortName: string | null) => void,
}

export function NationalityDropdown(props: NationalityDropdownProps) {
    const app = useContext(AppContext);
    const invalid = props.required && !props.selectedId;

    const options: NationalitySelectOption[] = props.nationalities.map(n => ({
        value: n.ID,
        label: n.Name,
        shortName: n.ShortName,
        iso: n.ISO_3166_1_Alpha_3_Code,
    }));

    if (props.selectedId && !options.some(o => o.value === props.selectedId)) {
        options.unshift({
            value: props.selectedId,
            label: props.nationalityText || "Current nationality",
            shortName: props.nationalityText ?? "",
            iso: "",
        });
    }

    const selected = options.find(o => o.value === props.selectedId) ?? null;

    const style: StylesConfig<NationalitySelectOption, false> = {
        option: (styles, { isFocused, isSelected }) => ({
            ...styles,
            backgroundColor: isSelected
                ? app.styleColourAlternative
                : isFocused
                    ? app.styleColourSecondary
                    : `#fff`,
            color: isSelected || isFocused ? `#fff` : `#000`,
        }),
        control: (styles) => ({
            ...styles,
            ...(props.submitting && invalid
                ? { borderColor: "#dc3545", boxShadow: "none" }
                : {}),
        }),
        menuPortal: (styles) => ({
            ...styles,
            zIndex: 9999,
        }),
    };

    return (
        <div className="col-lg-4" key="InputControl_Nationality">
            <label htmlFor="control_Nationality" className="form-label-alt">
                {props.required && <><RequiredIndicator isOk={!invalid} /> </>}Nationality
            </label>
            <Select<NationalitySelectOption>
                inputId="control_Nationality"
                aria-label="Nationality"
                name="Nationality"
                placeholder="Please select"
                isSearchable
                isClearable
                isLoading={props.loading}
                isDisabled={props.submitting}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={style}
                options={options}
                value={selected}
                onChange={(opt: SingleValue<NationalitySelectOption>) => {
                    props.onChange(opt?.value ?? null, opt?.shortName ?? null);
                }}
                filterOption={(option, input) => {
                    if (!input) {
                        return true;
                    }
                    const query = input.toLowerCase();
                    const data = option.data;
                    return option.label.toLowerCase().includes(query)
                        || (data.shortName ?? "").toLowerCase().includes(query)
                        || (data.iso ?? "").toLowerCase().includes(query);
                }}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary25: app.styleColourSecondary,
                        primary: app.styleColourSecondary,
                    },
                })}
            />
            {props.submitting && invalid && (
                <div className="invalid-feedback d-block">
                    Nationality required
                </div>
            )}
        </div>
    );
}
